/**
 * Hook to get the current AI model being used for chat
 * Returns the effective model name for display in caution messages
 */
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api/axiosInstance';

interface ModelCategory {
  id: string;
  name: string;
  description: string;
  tier: string;
  is_default: boolean;
  selected_model: string | null;
}

interface ModelCategoriesResponse {
  categories: ModelCategory[];
  provider: {
    id: string;
    name: string;
  } | null;
  accepted_mime_types?: string[];
}

interface UserSettings {
  effective_model_category: string;
  /** Skill-resolved model ID for the AI chat surface. Null if unresolvable. */
  effective_model_id: string | null;
  allow_user_model_choice: boolean;
}

async function fetchModelCategories(): Promise<ModelCategoriesResponse> {
  const response = await apiGet<{ data: ModelCategoriesResponse }>('/api/v1/ai-chat/settings/user/model-categories');
  return response.data;
}

async function fetchUserSettings(): Promise<UserSettings> {
  return apiGet<UserSettings>('/api/v1/ai-chat/settings/user');
}

/**
 * Returns the display name for the current AI model (e.g., "Claude Sonnet 4.5")
 */
export function useCurrentAIModel() {
  const { data: categories, error: categoriesError, isLoading: categoriesLoading } = useQuery({
    queryKey: ['chat-model-categories'],
    queryFn: fetchModelCategories,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1, // Only retry once on failure
  });

  const { data: settings, error: settingsError, isLoading: settingsLoading } = useQuery({
    queryKey: ['chat-user-settings'],
    queryFn: fetchUserSettings,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Prefer the skill-resolved model ID from /settings/user (cross-provider safe).
  // Fall back to looking up the category in the active-provider list (legacy path).
  const effectiveCategoryId = settings?.effective_model_category;
  const category = categories?.categories?.find(c => c.id === effectiveCategoryId);

  let modelName: string | null = null;
  if (settings?.effective_model_id) {
    modelName = formatModelName(settings.effective_model_id);
  } else if (category?.selected_model) {
    modelName = formatModelName(category.selected_model);
  }

  // Determine error state
  const isLoading = categoriesLoading || settingsLoading;
  const error = categoriesError || settingsError;

  // If data loaded but we couldn't determine the model, that's an error
  const modelError = !isLoading && !error && !modelName
    ? new Error('Could not determine current AI model')
    : null;

  return {
    modelName,
    categoryName: category?.name ?? null,
    providerName: categories?.provider?.name ?? null,
    allowModelChoice: settings?.allow_user_model_choice ?? false,
    /** Default accepted MIME types from the provider (before a session is created) */
    defaultAcceptedMimeTypes: categories?.accepted_mime_types ?? [],
    isLoading,
    error: error || modelError,
  };
}

/**
 * Format model ID to human-readable name
 * Examples:
 *   "claude-sonnet-4-20250514" -> "Claude Sonnet 4"
 *   "claude-sonnet-4-5-20250929" -> "Claude Sonnet 4.5"
 *   "claude-3-5-haiku-20241022" -> "Claude Haiku 3.5"
 *   "claude-opus-4-20250514" -> "Claude Opus 4"
 *   "gpt-4o-2024-08-06" -> "GPT-4o"
 *   "gemini-1.5-pro-002" -> "Gemini 1.5 Pro"
 */
export function formatModelName(modelId: string): string {
  // Remove date suffix (e.g., "-20250514" or "-2024-08-06")
  let name = modelId.replace(/-\d{8}$/, '').replace(/-\d{4}-\d{2}-\d{2}$/, '');
  // Remove version suffix like -002
  name = name.replace(/-\d{3}$/, '');

  // Handle Claude models
  if (name.startsWith('claude-')) {
    name = name.replace('claude-', '');

    // Handle versioned patterns like "3-5-haiku" -> version 3.5, model haiku
    const versionMatch = name.match(/^(\d+)-(\d+)-(.+)$/);
    if (versionMatch) {
      const [, major, minor, modelType] = versionMatch;
      const formattedModel = modelType.charAt(0).toUpperCase() + modelType.slice(1);
      return `Claude ${formattedModel} ${major}.${minor}`;
    }

    // Handle patterns like "sonnet-4-5" -> "Sonnet 4.5" or "opus-4-6" -> "Opus 4.6"
    const modelVersionMatch = name.match(/^([a-z]+)-(\d+)-(\d+)$/);
    if (modelVersionMatch) {
      const [, modelType, major, minor] = modelVersionMatch;
      const formattedModel = modelType.charAt(0).toUpperCase() + modelType.slice(1);
      return `Claude ${formattedModel} ${major}.${minor}`;
    }

    // Handle patterns like "sonnet-4" or "opus-4"
    const simpleMatch = name.match(/^([a-z]+)-(\d+(?:\.\d+)?)$/);
    if (simpleMatch) {
      const [, modelType, version] = simpleMatch;
      const formattedModel = modelType.charAt(0).toUpperCase() + modelType.slice(1);
      return `Claude ${formattedModel} ${version}`;
    }

    // Handle patterns like "3-opus" (older naming)
    const oldMatch = name.match(/^(\d+)-([a-z]+)$/);
    if (oldMatch) {
      const [, version, modelType] = oldMatch;
      const formattedModel = modelType.charAt(0).toUpperCase() + modelType.slice(1);
      return `Claude ${formattedModel} ${version}`;
    }

    // Fallback: just capitalize parts
    const parts = name.split('-').map(part => {
      if (/^\d/.test(part)) return part;
      return part.charAt(0).toUpperCase() + part.slice(1);
    });
    return 'Claude ' + parts.join(' ');
  }

  // Handle GPT models
  if (name.startsWith('gpt-')) {
    // "gpt-4o" -> "GPT-4o", "gpt-4o-mini" -> "GPT-4o Mini"
    const parts = name.split('-');
    const gptPart = parts.slice(0, 2).join('-').toUpperCase();
    const rest = parts.slice(2).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
    return rest ? `${gptPart} ${rest}` : gptPart;
  }

  // Handle Gemini models
  if (name.startsWith('gemini-')) {
    // "gemini-1.5-pro" -> "Gemini 1.5 Pro"
    const parts = name.replace('gemini-', '').split('-');
    const formatted = parts.map(part => {
      if (/^\d/.test(part)) return part;
      return part.charAt(0).toUpperCase() + part.slice(1);
    });
    return 'Gemini ' + formatted.join(' ');
  }

  // Handle o1/o3 models
  if (name.startsWith('o1') || name.startsWith('o3')) {
    const parts = name.split('-');
    return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
  }

  // Default: capitalize first letter of each word
  return name.split('-').map(part =>
    part.charAt(0).toUpperCase() + part.slice(1)
  ).join(' ');
}
